import * as React from "react";
import {
  type AriaListBoxOptions,
  FocusRing,
  useCollator,
  useListBox,
  useListBoxSection,
  useLocale,
  useOption,
} from "react-aria";
import { type ListState } from "react-stately";
import { CheckIcon } from "@heroicons/react/24/solid";
import { useLayoutEffect, useObjectRef } from "@react-aria/utils";
import {
  layoutInfoToStyle,
  useVirtualizerItem,
  Virtualizer,
  VirtualizerItem,
  VirtualizerItemOptions,
} from "@react-aria/virtualizer";
import { ListLayout } from "@react-stately/layout";
import { LayoutInfo, ReusableView } from "@react-stately/virtualizer";
import type { Node } from "@react-types/shared";
import clsx from "clsx";

import { Spinner } from "~/components/Loader";
import { Text } from "~/components/Text";

interface ListBoxProps<T> extends AriaListBoxOptions<T> {
  listBoxRef?: React.RefObject<HTMLDivElement>;
  state: ListState<T>;
  isLoading?: boolean;
}

function useListBoxLayout<T>(state: ListState<T>, isLoading: boolean) {
  let collator = useCollator({ usage: "search", sensitivity: "base" });
  let layout = React.useMemo(
    () =>
      new ListLayout<T>({
        estimatedRowHeight: 44,
        estimatedHeadingHeight: 24,
        padding: 0,
        loaderHeight: 44,
        placeholderHeight: 44,
        collator,
      }),
    [collator],
  );

  layout.collection = state.collection;
  layout.disabledKeys = state.disabledKeys;

  useLayoutEffect(() => {
    // Sync loading state into the layout.
    if (layout.isLoading !== isLoading) {
      layout.isLoading = isLoading;
      layout.virtualizer?.relayoutNow();
    }
  }, [layout, isLoading]);

  return layout;
}

//@ts-ignore
const ListBoxContext = React.createContext<ListState<unknown>>(null);

export function ListBox<T>({
  maxHeight = "max-h-80",
  ...props
}: ListBoxProps<T> & {
  transitionDuration?: number;
  maxHeight?: string;
  renderEmptyState?: () => JSX.Element;
}) {
  let { shouldUseVirtualFocus, transitionDuration = 0 } = props;

  let ref = useObjectRef(props.listBoxRef);

  let { state } = props;
  let layout = useListBoxLayout(state, props.isLoading ?? false);
  let { listBoxProps } = useListBox(
    {
      ...props,
      isVirtualized: true,
      keyboardDelegate: layout,
    },
    state,
    ref,
  );

  type View = ReusableView<Node<T>, unknown>;
  let renderWrapper = (
    parent: View | null,
    reusableView: View,
    children: View[],
    renderChildren: (views: View[]) => React.ReactElement[],
  ) => {
    if (reusableView.viewType === "section") {
      return (
        <VirtualizedSection
          headerLayoutInfo={children.find((c) => c.viewType === "header")!.layoutInfo!}
          item={reusableView.content}
          key={reusableView.key}
          layoutInfo={reusableView.layoutInfo!}
          virtualizer={reusableView.virtualizer}
        >
          {renderChildren(children.filter((c) => c.viewType === "item"))}
        </VirtualizedSection>
      );
    }

    return (
      <VirtualizerItem
        key={reusableView.key}
        layoutInfo={reusableView.layoutInfo!}
        parent={parent?.layoutInfo!}
        virtualizer={reusableView.virtualizer}
      >
        {reusableView.rendered as any}
      </VirtualizerItem>
    );
  };

  return (
    <ListBoxContext.Provider value={state}>
      <Virtualizer
        {...listBoxProps}
        autoFocus={!!props.autoFocus}
        className={clsx("space-y-xs outline-none", maxHeight)}
        collection={state.collection}
        focusedKey={state.selectionManager.focusedKey}
        isLoading={props.isLoading}
        layout={layout}
        ref={ref}
        renderWrapper={renderWrapper}
        scrollDirection="vertical"
        shouldUseVirtualFocus={shouldUseVirtualFocus}
        sizeToFit="height"
        transitionDuration={transitionDuration}
      >
        {(type: string, item: Node<T>) => {
          if (type === "item") {
            return <VirtualizedOption item={item} />;
          }
          if (type === "section") {
            return null;
          }
          if (type === "loader") {
            return (
              <div className="flex items-center justify-center outline-none">
                <Spinner />
              </div>
            );
          }

          if (type === "placeholder") {
            let emptyState = props.renderEmptyState ? (
              props.renderEmptyState()
            ) : (
              <div>
                <Text i18nKey="util.validation.no_results" />
              </div>
            );

            return (
              <div
                // aria-selected isn't needed here since this option is not selectable.
                // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
                aria-selected="false"
                className={clsx(
                  "flex items-center justify-between",
                  "text-gray-500",
                  "mx-xs p-xs",
                  "rounded-md",
                  "cursor-default outline-none",
                )}
                role="option"
              >
                {emptyState}
              </div>
            );
          }

          console.warn(`Unknown type: ${type}`);
          console.warn(item);

          return null;
        }}
      </Virtualizer>
    </ListBoxContext.Provider>
  );
}

interface VirtualSectionProps<T> extends Omit<VirtualizerItemOptions, "ref"> {
  headerLayoutInfo: LayoutInfo;
  item: Node<T>;
  children?: React.ReactNode;
}

function VirtualizedSection<T>(props: VirtualSectionProps<T>) {
  let { children, headerLayoutInfo, item, layoutInfo, virtualizer } = props;

  let { groupProps, headingProps } = useListBoxSection({
    heading: item.rendered,
    "aria-label": item["aria-label"],
  });

  let headerRef = React.useRef<HTMLDivElement>(null);

  useVirtualizerItem({
    layoutInfo: headerLayoutInfo,
    virtualizer,
    ref: headerRef,
  });

  let { direction } = useLocale();

  return (
    <>
      <div
        ref={headerRef}
        role="presentation"
        style={layoutInfoToStyle(headerLayoutInfo, direction)}
      >
        {item.rendered && (
          <div {...headingProps} className="font-bold uppercase text-gray-500">
            {item.rendered}
          </div>
        )}
      </div>
      <div {...groupProps} style={layoutInfoToStyle(layoutInfo, direction)}>
        {children}
      </div>
    </>
  );
}

interface VirtualOptionProps<T> {
  item: Node<T>;
}

function VirtualizedOption<T>(props: VirtualOptionProps<T>) {
  let state = React.useContext(ListBoxContext);

  let { item } = props;

  let { key, rendered } = item;

  let ref = React.useRef<HTMLDivElement>(null);

  let { isFocused, isSelected, optionProps } = useOption(
    {
      "aria-label": item["aria-label"],
      key,
      shouldFocusOnHover: true,
    },
    state,
    ref,
  );

  if (!state.collection.getItem(props.item.key)) {
    return null;
  }

  return (
    <FocusRing>
      <div
        {...optionProps}
        className={clsx(
          "flex items-center justify-between",
          "mx-xs p-xs",
          "rounded-md",
          "cursor-default outline-none",
          isFocused ? "bg-brand-dark text-white" : "bg-transparent text-gray-700",
          isSelected && "font-semibold",
        )}
        ref={ref}
      >
        {rendered}
        {isSelected && (
          <CheckIcon
            aria-hidden="true"
            className={clsx("h-[20px] w-[20px]", isFocused ? "text-white" : "text-gray-700")}
          />
        )}
      </div>
    </FocusRing>
  );
}
